import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "./apiClient";

/**
 * Help center data — endpointy `/admin/api/v1/docs/*`. Obsah renderuje server-side Flexmark
 * ({@code DocsService}); SPA jen zobrazuje předrenderovaný HTML + TOC + sidebar.
 * Filtrace (customer profil + licence + locale) probíhá na serveru — locale se
 * posílá přes `Accept-Language` (apiClient).
 */

export interface DocsPageRef {
  slug: string;
  title: string;
  feature: string | null;
}

export interface DocsSection {
  id: string;
  title: string;
  pages: DocsPageRef[];
}

export interface DocsIndex {
  defaultSlug: string;
  sections: DocsSection[];
}

export interface DocsHeading {
  level: number;
  text: string;
  anchor: string;
}

export interface DocsPageData {
  slug: string;
  sectionId: string;
  title: string;
  htmlContent: string;
  headings: DocsHeading[];
  locale: string;
  notTranslated: boolean;
  lockedBanner: string | null;
}

export interface DocsSearchHeading {
  text: string;
  anchor: string;
}

export interface DocsSearchEntry {
  slug: string;
  title: string;
  sectionTitle: string;
  headings: DocsSearchHeading[];
  snippet: string;
}

/** Sidebar strom + výchozí stránka. */
export function useDocsIndex() {
  return useQuery({
    queryKey: ["docs", "index"],
    queryFn: () => apiFetch<DocsIndex>("/admin/api/v1/docs"),
    staleTime: 5 * 60_000,
  });
}

/** Jedna renderovaná stránka. `enabled` až když je slug známý. */
export function useDocsPage(slug: string | undefined) {
  return useQuery({
    queryKey: ["docs", "page", slug],
    queryFn: () => apiFetch<DocsPageData>(`/admin/api/v1/docs/pages/${encodeURIComponent(slug!)}`),
    enabled: !!slug,
    staleTime: 5 * 60_000,
  });
}

/** Vyhledávací index (klientský filtr). Sdílený s Thymeleaf help centrem. */
export function useDocsSearchIndex() {
  return useQuery({
    queryKey: ["docs", "search-index"],
    queryFn: () => apiFetch<DocsSearchEntry[]>("/admin/api/v1/docs/search-index"),
    staleTime: 5 * 60_000,
  });
}

/** Klientské fulltextové skóre nad indexem (title > sectionTitle/headings > snippet). */
export function searchDocs(index: DocsSearchEntry[] | undefined, query: string): DocsSearchEntry[] {
  const q = query.trim().toLowerCase();
  if (!q || !index) return [];
  const scored: { entry: DocsSearchEntry; score: number }[] = [];
  for (const entry of index) {
    let score = 0;
    if (entry.title.toLowerCase().includes(q)) score += 10;
    if (entry.sectionTitle.toLowerCase().includes(q)) score += 3;
    if (entry.headings.some((h) => h.text.toLowerCase().includes(q))) score += 4;
    if (entry.snippet.toLowerCase().includes(q)) score += 1;
    if (score > 0) scored.push({ entry, score });
  }
  return scored.sort((a, b) => b.score - a.score).map((s) => s.entry);
}
